/**
 * Cached Forms lookups (statuses, field types, categories, series, member fields...).
 */

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useFormsService, usePublicFormsService, type FormsLookupsView } from '@actschurch/ccms-sdk';

export const formsKeys = {
  all: ['forms'] as const,
  lookups: () => ['forms', 'lookups'] as const,
  dashboard: () => ['forms', 'dashboard'] as const,
  list: (filters: unknown) => ['forms', 'list', filters] as const,
  detail: (id: number) => ['forms', 'detail', id] as const,
  fields: (id: number, allVersions: boolean) => ['forms', 'fields', id, allVersions] as const,
  share: (id: number) => ['forms', 'share', id] as const,
  analytics: (id: number, days: number) => ['forms', 'analytics', id, days] as const,
  versions: (id: number) => ['forms', 'versions', id] as const,
  version: (id: number, versionId: number) => ['forms', 'version', id, versionId] as const,
  apiAccess: (id: number) => ['forms', 'api-access', id] as const,
  inboundTokens: (id: number) => ['forms', 'inbound-tokens', id] as const,
  capacity: (id: number) => ['forms', 'capacity', id] as const,
  collaborators: (id: number) => ['forms', 'collaborators', id] as const,
  uniqueRules: (id: number) => ['forms', 'unique-rules', id] as const,
  entries: (filters: unknown) => ['forms', 'entries', filters] as const,
  entry: (id: number) => ['forms', 'entry', id] as const,
  memberCandidates: (id: number) => ['forms', 'entry', id, 'member-candidates'] as const,
  webhooks: () => ['forms', 'webhooks'] as const,
  deliveries: (id: number, page: number) => ['forms', 'webhooks', id, 'deliveries', page] as const,
  moduleSettings: () => ['forms', 'module-settings'] as const,
  categories: () => ['forms', 'categories'] as const,
  series: () => ['forms', 'series'] as const,
};

export function useFormsLookups() {
  const formsService = useFormsService();
  const query = useQuery<FormsLookupsView>({
    queryKey: formsKeys.lookups(),
    queryFn: () => formsService.getLookups(),
    staleTime: 5 * 60_000,
  });

  const options = useMemo(() => {
    const l = query.data;
    return {
      formStatuses: (l?.formStatuses ?? []).map((s) => ({ value: String(s.statusID), label: s.name ?? '' })),
      entryStatuses: (l?.entryStatuses ?? []).map((s) => ({ value: String(s.statusID), label: s.name ?? '' })),
      categories: (l?.categories ?? [])
        .filter((c) => c.isActive !== false)
        .map((c) => ({ value: String(c.formCategoryID), label: c.name ?? '' })),
      series: (l?.series ?? [])
        .filter((s) => s.isActive !== false)
        .map((s) => ({ value: String(s.formSeriesID), label: s.name ?? '' })),
      webhookEvents: (l?.webhookEventTypes ?? []).map((e) => ({ value: e.code ?? '', label: e.name || e.code || '' })),
      uniqueRuleActions: (l?.uniqueRuleActions ?? []).map((a) => ({ value: String(a.actionID), label: a.name ?? '' })),
      uniqueRuleScopes: (l?.uniqueRuleScopes ?? []).map((a) => ({ value: String(a.scopeID), label: a.name ?? '' })),
      memberFields: l?.memberFields ?? [],
    };
  }, [query.data]);

  return { ...query, lookups: query.data, options };
}

/**
 * Campus + department options for form metadata pickers — read through the Forms
 * service's own lookup endpoints ({ id, name }) so the module never calls another
 * service's tables directly.
 */
export function useFormsOrgOptions() {
  const publicForms = usePublicFormsService();
  const locations = useQuery({
    queryKey: ['forms', 'org', 'locations'],
    queryFn: () => publicForms.getLookups('locations'),
    staleTime: 10 * 60_000,
  });
  const departments = useQuery({
    queryKey: ['forms', 'org', 'departments'],
    queryFn: () => publicForms.getLookups('departments'),
    staleTime: 10 * 60_000,
  });
  return {
    locationOptions: (locations.data ?? []).map((o) => ({ value: String(o.id), label: o.name })),
    departmentOptions: (departments.data ?? []).map((o) => ({ value: String(o.id), label: o.name })),
  };
}
